import { useMutation, useQuery } from "@tanstack/react-query";
import { BarChart3, History, Sun } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { AssessmentProgressPill } from "@/components/briefing/AssessmentProgressPill";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { BriefingCallTimeSection } from "@/components/briefing/BriefingCallTimeSection";
import { BriefingCard } from "@/components/briefing/BriefingCard";
import { BriefingConversation } from "@/components/briefing/BriefingConversation";
import { BriefingEmpty } from "@/components/briefing/BriefingEmpty";
import { BriefingHistory } from "@/components/briefing/BriefingHistory";
import { BriefingProgress } from "@/components/briefing/BriefingProgress";
import { BriefingStatusBadge } from "@/components/briefing/BriefingStatusBadge";
import { PersonalityAssessmentItem } from "@/components/briefing/PersonalityAssessmentItem";
import { EmailPerformanceCard } from "@/components/briefing/EmailPerformanceCard";
import { EmailSuggestionCard } from "@/components/briefing/EmailSuggestionCard";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	useBriefingConversationQuery,
	useBriefingsQuery,
	useGenerateBriefing,
	useSendBriefingMessage,
	useTodayBriefingQuery,
	useUpdateBriefingItem,
	useUpdateBriefingStatus,
} from "@/hooks/use-briefing";
import { useCallTimeWeeklySummary } from "@/hooks/use-call-time";
import {
	useCreateFromSuggestion,
	useEmailBriefingData,
} from "@/hooks/use-email-campaigns";
import { VALID_SESSION_IDS } from "@/lib/action-routes";
import { trackBriefingStarted } from "@/lib/analytics-events";
import { useSession } from "@/hooks/use-session";
import { useStrategyStatus } from "@/hooks/use-strategy";
import { StrategyGeneratingState } from "@/components/briefing/StrategyGeneratingState";
import { apiService } from "@/lib/api-service";
import {
	type Briefing,
	BriefingItemType,
	type BriefingItemUpdate,
	type PersonalityProfileSummary,
	type EmailBriefingSuggestion,
	type FollowUpSuggestion,
	BriefingStatus
} from "@/types/briefing";

export const Route = createFileRoute("/_app/_/daily-briefing/")({
	component: DailyBriefingPage,
	validateSearch: (search: Record<string, unknown>): { session?: string } => ({
		session: search.session ? String(search.session) : undefined,
	}),
});

function DailyBriefingPage() {
	const session = useSession();
	const campaignId = session.campaign?.id;
	const { session: scrollToSession } = Route.useSearch();

	const [selectedBriefing, setSelectedBriefing] = useState<Briefing | null>(
		null,
	);

	const { data: profileData } = useQuery({
		queryKey: ["personality-profile", campaignId],
		queryFn: () =>
			apiService.get<PersonalityProfileSummary>(
				`/campaigns/${campaignId}/personality-profile`,
			),
		enabled: !!campaignId,
		staleTime: 5 * 60 * 1000,
	});

	if (!campaignId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-4xl">
					<p className="text-muted-foreground">Loading campaign...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-muted/30 p-4 md:p-6">
			<div className="mx-auto max-w-4xl space-y-6">
				{/* Page header */}
				<div>
					<div className="flex items-center gap-2">
						<Sun className="size-5" />
						<h1 className="text-2xl font-semibold">Daily Briefing</h1>
						<AssessmentProgressPill profile={profileData ?? null} />
					</div>
					<p className="text-sm text-muted-foreground">
						Your daily campaign action plan
					</p>
				</div>

				<Tabs defaultValue="today" onValueChange={() => setSelectedBriefing(null)}>
					<TabsList>
						<TabsTrigger value="today">Today</TabsTrigger>
						<TabsTrigger value="history">
							<History className="mr-1.5 size-3.5" />
							History
						</TabsTrigger>
						<TabsTrigger value="progress">
							<BarChart3 className="mr-1.5 size-3.5" />
							Progress
						</TabsTrigger>
					</TabsList>
					<TabsContent value="today" className="mt-4">
						<TodayView campaignId={campaignId} scrollToSession={scrollToSession} />
					</TabsContent>
					<TabsContent value="history" className="mt-4">
						{selectedBriefing ? (
							<BriefingDetailView
								campaignId={campaignId}
								briefing={selectedBriefing}
								onBack={() => setSelectedBriefing(null)}
							/>
						) : (
							<HistoryView
								campaignId={campaignId}
								onSelect={(b) => setSelectedBriefing(b)}
							/>
						)}
					</TabsContent>
					<TabsContent value="progress" className="mt-4">
						<BriefingProgress campaignId={campaignId} />
					</TabsContent>
				</Tabs>
			</div>
		</div>
	);
}

// ---------------------------------------------------------------------------
// Today View
// ---------------------------------------------------------------------------

function TodayView({ campaignId, scrollToSession }: { campaignId: number; scrollToSession?: string }) {
	const {
		data: briefing,
		isLoading,
		error,
	} = useTodayBriefingQuery(campaignId);

	const { data: strategyStatus, isLoading: isStrategyLoading } =
		useStrategyStatus();

	const generateMutation = useGenerateBriefing(campaignId);
	const updateStatusMutation = useUpdateBriefingStatus(campaignId);
	const hasMarkedInProgress = useRef(false);

	// Auto-mark as in_progress when first viewed
	useEffect(() => {
		if (
			briefing &&
			briefing.status === BriefingStatus.Pending &&
			!hasMarkedInProgress.current
		) {
			hasMarkedInProgress.current = true;
			trackBriefingStarted();
			updateStatusMutation.mutate({
				briefingId: briefing.id,
				status: BriefingStatus.InProgress,
			});
		}
	}, [briefing, updateStatusMutation]);

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-20">
				<Spinner className="size-6" />
			</div>
		);
	}

	if (error || !briefing || briefing.briefing_items.length === 0) {
		// While strategy status is loading, show the spinner
		if (isStrategyLoading) {
			return (
				<div className="flex items-center justify-center py-20">
					<Spinner className="size-6" />
				</div>
			);
		}

		// If strategy is still generating or interview not done, show generating state
		const status = strategyStatus?.status;
		if (status === "generating" || status === "interview_pending") {
			return (
				<StrategyGeneratingState
					strategyStatus={strategyStatus}
					campaignId={campaignId}
				/>
			);
		}

		return (
			<BriefingEmpty
				onGenerate={() => generateMutation.mutate()}
				isGenerating={generateMutation.isPending}
			/>
		);
	}

	return <BriefingDetailView campaignId={campaignId} briefing={briefing} scrollToSession={scrollToSession} />;
}

// ---------------------------------------------------------------------------
// History View
// ---------------------------------------------------------------------------

function HistoryView({
	campaignId,
	onSelect,
}: {
	campaignId: number;
	onSelect: (briefing: Briefing) => void;
}) {
	const { data, isLoading } = useBriefingsQuery(campaignId, {
		page: 1,
		page_size: 20,
	});

	return (
		<BriefingHistory
			briefings={data?.briefings ?? []}
			onSelect={onSelect}
			isLoading={isLoading}
		/>
	);
}

// ---------------------------------------------------------------------------
// Briefing Detail View
// ---------------------------------------------------------------------------

function BriefingDetailView({
	campaignId,
	briefing,
	onBack,
	scrollToSession,
}: {
	campaignId: number;
	briefing: Briefing;
	onBack?: () => void;
	scrollToSession?: string;
}) {
	const navigate = useNavigate();
	// Auto-scroll to a specific session card when deep-linked
	useEffect(() => {
		if (!scrollToSession) return;
		if (!(VALID_SESSION_IDS as readonly string[]).includes(scrollToSession)) return;
		const timer = setTimeout(() => {
			const el = document.querySelector(`[data-session-id="${scrollToSession}"]`);
			if (el) {
				el.scrollIntoView({ behavior: "smooth", block: "center" });
				el.classList.add("ring-2", "ring-primary", "ring-offset-2");
				setTimeout(() => el.classList.remove("ring-2", "ring-primary", "ring-offset-2"), 3000);
			}
		}, 500);
		return () => clearTimeout(timer);
	}, [scrollToSession]);
	const updateItemMutation = useUpdateBriefingItem(campaignId);
	const conversationQuery = useBriefingConversationQuery(
		campaignId,
		briefing.id,
	);
	const sendMessageMutation = useSendBriefingMessage(campaignId);

	const skipAssessmentMutation = useMutation({
		mutationFn: (sessionId: number) =>
			apiService.post(
				`/campaigns/${campaignId}/assessment-sessions/${sessionId}/skip`,
			),
	});
	// Email briefing data
	const { data: emailBriefingData } = useEmailBriefingData(campaignId);
	const createFromSuggestion = useCreateFromSuggestion(campaignId);

	// Call time data
	const { data: callTimeSummary } = useCallTimeWeeklySummary(campaignId);

	const handleUpdateItem = (index: number, data: BriefingItemUpdate) => {
		updateItemMutation.mutate(
			{
				briefingId: briefing.id,
				itemIndex: index,
				data,
			},
			{
				onSuccess: (response: unknown) => {
					const res = response as { created_task_id?: number };
					if (res?.created_task_id) {
						toast.success("Task created from briefing item");
					}
				},
			},
		);
	};

	const handleSendMessage = (message: string) => {
		sendMessageMutation.mutate({
			briefingId: briefing.id,
			message,
		});
	};

	const handleSkipAssessment = (sessionId: number) => {
		skipAssessmentMutation.mutate(sessionId);
	};

	const handleStartAssessment = (_sessionId: number) => {
		toast.info("Assessment conversations coming soon");
	};
	const handleDraftEmail = (suggestion: EmailBriefingSuggestion) => {
		createFromSuggestion.mutate(
			{
				pre_drafted_purpose: suggestion.pre_drafted_purpose,
				suggested_audience: suggestion.suggested_audience,
				suggested_audience_id: suggestion.suggested_audience_id,
			},
			{
				onSuccess: (result) => {
					void navigate({
						to: "/contacts/email/$emailId",
						params: {
							emailId: String(result.email_campaign_id),
						},
					} as never);
				},
			},
		);
	};

	const handleFollowUp = (fu: FollowUpSuggestion) => {
		if (fu.pre_drafted_purpose) {
			createFromSuggestion.mutate(
				{
					pre_drafted_purpose: fu.pre_drafted_purpose,
				},
				{
					onSuccess: (result) => {
						void navigate({
							to: "/contacts/email/$emailId",
							params: {
								emailId: String(result.email_campaign_id),
							},
						} as never);
					},
				},
			);
		}
	};

	const handleShowEmailDetails = (emailId: number) => {
		void navigate({
			to: "/contacts/email/$emailId",
			params: { emailId: String(emailId) },
		} as never);
	};

	const handleSkipSuggestion = (_suggestion: EmailBriefingSuggestion) => {
		// Dismissal is handled locally in the card component
	};

	const pendingCount = briefing.briefing_items.filter(
		(i) => i.status === "pending",
	).length;

	const emailSuggestions = emailBriefingData?.suggestions ?? [];
	const emailPerformance = emailBriefingData?.performance;

	return (
		<div className="space-y-6">
			{/* Back button for history navigation */}
			{onBack && (
				<button
					type="button"
					onClick={onBack}
					className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
				>
					&larr; Back to history
				</button>
			)}

			{/* Briefing meta */}
			<div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
				<div className="flex items-center gap-3">
					<p className="text-sm font-medium">
						{new Date(briefing.briefing_date).toLocaleDateString("en-US", {
							weekday: "long",
							month: "long",
							day: "numeric",
							year: "numeric",
						})}
					</p>
					<BriefingStatusBadge status={briefing.status} />
				</div>
				{pendingCount > 0 && (
					<p className="text-xs text-muted-foreground">
						{pendingCount} of {briefing.briefing_items.length} items pending
					</p>
				)}
			</div>

			{/* Opening message */}
			{briefing.opening_message && (
				<div className="rounded-lg bg-muted/50 px-4 py-3">
					<p className="text-sm leading-relaxed text-foreground">
						{briefing.opening_message}
					</p>
				</div>
			)}

			{/* Email performance card */}
			{emailPerformance && emailPerformance.has_data && (
				<EmailPerformanceCard
					performance={emailPerformance}
					onFollowUp={handleFollowUp}
					onShowDetails={handleShowEmailDetails}
					isActing={createFromSuggestion.isPending}
				/>
			)}

			{/* Call Time section */}
			{(briefing.call_time_recommendations?.length ?? 0) > 0 && (
				<BriefingCallTimeSection
					recommendations={briefing.call_time_recommendations ?? []}
					weeklySummary={callTimeSummary}
				/>
			)}

			{/* Briefing items */}
			<div className="space-y-3">
				{briefing.briefing_items.map((item, index) =>
					item.item_type ===
					BriefingItemType.PersonalityAssessment ? (
						<PersonalityAssessmentItem
							key={`${item.item_type}-${item.personality_session_id}-${index}`}
							item={item}
							campaignId={campaignId}
							onSkip={handleSkipAssessment}
							onStart={handleStartAssessment}
							isSkipping={skipAssessmentMutation.isPending}
						/>
					) : (
						<BriefingCard
							key={`${item.item_type}-${item.title}-${index}`}
							item={item}
							index={index}
							onUpdate={handleUpdateItem}
							isUpdating={updateItemMutation.isPending}
						/>
					),
				)}
			</div>

			{/* Email suggestion cards */}
			{emailSuggestions.length > 0 && (
				<div className="space-y-3">
					<h3 className="text-sm font-medium text-muted-foreground">
						Email Suggestions
					</h3>
					{emailSuggestions.map((suggestion) => (
						<EmailSuggestionCard
							key={`${suggestion.type}-${suggestion.title}`}
							suggestion={suggestion}
							onDraft={handleDraftEmail}
							onSkip={handleSkipSuggestion}
							isDrafting={createFromSuggestion.isPending}
						/>
					))}
				</div>
			)}

			{/* Closing message */}
			{briefing.closing_message && (
				<div className="rounded-lg bg-muted/50 px-4 py-3">
					<p className="text-sm leading-relaxed text-foreground">
						{briefing.closing_message}
					</p>
				</div>
			)}

			{/* Conversation panel */}
			<BriefingConversation
				messages={conversationQuery.data?.messages ?? []}
				onSend={handleSendMessage}
				isSending={sendMessageMutation.isPending}
				isLoading={conversationQuery.isLoading}
			/>
		</div>
	);
}
